import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import SignInButton from '../ui/SignInButton';

const navLinks = ['See the difference', 'Features', 'How it works', 'Pricing', 'Contact'];

export default function PulseNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();

  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(20px)',
      borderBottom: mobileOpen ? 'none' : '1px solid rgba(255,255,255,0.08)',
    }}>

      {/* Header row — fixed height, never affected by drawer */}
      <div style={{ display: 'flex', alignItems: 'center', height: 68 }}>

        {/* Back arrow — always left, always vertically centered */}
        <Link to="/" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 64, height: '100%', textDecoration: 'none', flexShrink: 0,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </Link>

        <nav style={{ flex: 1, minWidth: 0 }}>
          <div style={{ maxWidth: 1200, margin: '0 45px', height: '100%', display: 'flex', alignItems: 'center' }} className="pulse-nav-inner">

            {/* Pulse brand */}
            <a href="#" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', marginRight: 'auto' }}>
              <div style={{
                width: 34, height: 34, borderRadius: 9,
                background: 'linear-gradient(135deg,#479EC3,#3B88A9)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                  <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16"
                    stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
              </div>
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em', color: '#fff' }}>Pulse</div>
                <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>by Reano</div>
              </div>
            </a>

            {/* Desktop links */}
            <ul style={{ display: 'flex', gap: 2, listStyle: 'none', padding: 0, margin: 0 }} className="pulse-nav-desktop">
              {navLinks.map(link => (
                <li key={link}>
                  <a href={`#${link.toLowerCase().replace(/\s+/g, '-')}`}
                    className="pulse-nav-link pulse-nav-link--hero">
                    {link}
                  </a>
                </li>
              ))}
            </ul>

            {/* Divider */}
            <div className="pulse-nav-desktop" style={{ width: 1, height: 22, background: 'rgba(255,255,255,0.15)', marginRight: 16, flexShrink: 0 }} />

            {/* CTA */}
            <div className="pulse-nav-desktop" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {user ? (
                <>
                  <Link to="/dashboard" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '8px 16px', borderRadius: 9, textDecoration: 'none',
                    background: '#0f172a', border: '1.5px solid rgba(34,211,238,0.45)',
                    color: '#fff', fontSize: 13.5, fontWeight: 700, letterSpacing: '-.01em',
                    boxShadow: '0 0 14px rgba(34,211,238,0.2)',
                    transition: 'box-shadow .2s, transform .15s, border-color .2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow='0 0 24px rgba(34,211,238,0.4)'; e.currentTarget.style.borderColor='rgba(34,211,238,0.75)'; e.currentTarget.style.transform='translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow='0 0 14px rgba(34,211,238,0.2)'; e.currentTarget.style.borderColor='rgba(34,211,238,0.45)'; e.currentTarget.style.transform='none'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    Dashboard
                  </Link>
                  <button onClick={logout} style={{
                    padding: '8px 18px', borderRadius: 9, cursor: 'pointer',
                    background: 'transparent', border: '1.5px solid rgba(255,255,255,0.25)',
                    color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 600,
                    transition: 'border-color .2s, color .2s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.55)'; e.currentTarget.style.color='#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,0.25)'; e.currentTarget.style.color='rgba(255,255,255,0.75)'; }}
                  >Logout</button>
                </>
              ) : (
                <SignInButton />
              )}
            </div>

            {/* Hamburger */}
            <button onClick={() => setMobileOpen(o => !o)} className="pulse-nav-hamburger"
              style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 6, paddingRight: 32 }}>
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8">
                {mobileOpen
                  ? <><line x1="4" y1="4" x2="18" y2="18"/><line x1="18" y1="4" x2="4" y2="18"/></>
                  : <><line x1="3" y1="7" x2="19" y2="7"/><line x1="3" y1="11" x2="19" y2="11"/><line x1="3" y1="15" x2="19" y2="15"/></>}
              </svg>
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile drawer — rendered below the header row, never inside it */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            style={{ overflow: 'hidden', background: '#0f172a', borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '14px 24px 20px' }}>
              {navLinks.map(link => (
                <a key={link} href={`#${link.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => setMobileOpen(false)}
                  style={{ display: 'block', padding: '11px 0', fontSize: 15, fontWeight: 500, color: 'rgba(255,255,255,0.75)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  {link}
                </a>
              ))}
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileOpen(false)} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '12px 0', borderRadius: 10, textDecoration: 'none',
                      background: '#0f172a', border: '1.5px solid rgba(34,211,238,0.45)',
                      color: '#fff', fontSize: 15, fontWeight: 700,
                      boxShadow: '0 0 16px rgba(34,211,238,0.2)',
                    }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                      </svg>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileOpen(false); }} style={{
                      padding: '12px 0', borderRadius: 10, cursor: 'pointer',
                      background: 'transparent', border: '1.5px solid rgba(255,255,255,0.2)',
                      color: 'rgba(255,255,255,0.7)', fontSize: 15, fontWeight: 600,
                    }}>Logout</button>
                  </>
                ) : (
                  <SignInButton onClick={() => setMobileOpen(false)} style={{ display: 'flex', justifyContent: 'center', fontSize: 15 }} />
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .pulse-nav-link { display: block; padding: 6px 14px; border-radius: 8px; font-size: 14px; font-weight: 500; text-decoration: none; transition: color .15s, background .15s; }
        .pulse-nav-link--hero         { color: rgba(255,255,255,0.82); }
        .pulse-nav-link--hero:hover   { color: #fff; background: rgba(255,255,255,0.14); }
        @media (max-width: 800px) {
          .pulse-nav-inner     { margin: 0 !important; }
          .pulse-nav-desktop   { display: none !important; }
          .pulse-nav-hamburger { display: flex !important; }
        }
      `}</style>
    </div>
  );
}
